import React, { useEffect, useRef, useContext } from 'react';
import { object, string, array, func } from 'prop-types';
import { TextStyled } from '@nike/nike-design-system-components';
import { CaretDown } from '@nike/nike-design-system-icons';
import Highlight from '../Highlight';
import useLocale from '../../utils/hooks/localeHook';
import {
  StyledLink,
  StyledListItem,
  accessibilityButton,
  searchSuggestionLinkAndButton,
} from './styles';
import { getSearchUrl } from '../../utils/url';
import { AnalyticsContext } from '../../context/analyticsContext';
import useBodega from '../../utils/hooks/bodegaHook';
import { BODEGA_DEFAULTS } from '../../constants';
import templateString from '../../utils/templateString';
import useDebounce from '../../utils/hooks/debounceHook';

const VisualSearchTopSuggestionsItem = ({
  searchResult,
  searchTerm,
  selectedSuggestion,
  setSelectedSuggestion,
  analyticsClient,
  searchResultsRef,
}) => {
  const { displayText } = searchResult;
  const locale = useLocale();
  const linkRef = useRef(null);
  const showProductSuggestionsLabel = templateString(
    useBodega('nav_show_product_suggestions', BODEGA_DEFAULTS.SHOW_PRODUCT_SUGGESTIONS),
    { selectedSuggestion: displayText },
  );
  const isSelected = searchResult === selectedSuggestion;

  const handleAccessibilityButtonClick = () => {
    if (searchResultsRef?.current) {
      searchResultsRef.current.focus();
    }
  };

  function handleFocus() {
    setSelectedSuggestion(searchResult);
  }

  function handleMouseEnter() {
    setSelectedSuggestion(searchResult);
  }

  const handleClick = () => {
    analyticsClient?.clickSearchTypeAheadSuggestions({ searchTerm });
  };

  return (
    <StyledListItem aria-selected={isSelected} isSelected={isSelected} role="option">
      <div className={searchSuggestionLinkAndButton}>
        <TextStyled
          Component={StyledLink}
          appearance="title4"
          color="secondary"
          href={getSearchUrl({ locale, searchTerm, query: displayText, path: 'w' })}
          onClick={handleClick}
          onFocus={() => handleFocus(true)}
          onMouseEnter={handleMouseEnter}
          path={searchTerm}
          ref={linkRef}
          rel="nofollow"
        >
          <Highlight searchTerm={searchTerm} textToHighlight={displayText} />
        </TextStyled>
        <button
          aria-label={showProductSuggestionsLabel}
          className={accessibilityButton}
          onClick={handleAccessibilityButtonClick}
          onFocus={() => handleFocus(false)}
        >
          <CaretDown aria-hidden={true} />
        </button>
      </div>
    </StyledListItem>
  );
};

/**
 * Sends resulting search term to analytics
 * @param {string} searchTerm
 */
function useSuggestionShownAnalytics(searchTerm) {
  const { analyticsClient } = useContext(AnalyticsContext);
  const debouncedSearchTerm = useDebounce(searchTerm);

  useEffect(() => {
    analyticsClient?.typeAheadSuggestionShown({ searchTerm });
  }, [
    debouncedSearchTerm, // using debounced search term because the results are debounced
  ]);
}

/**
 * List of top search suggestions to be displayed for a searched term.
 * @param {object} props
 * @param {array} props.searchResultsData search results data based on search term.
 * @param {object} props.searchResultsRef reference to the search results list.
 * @returns
 */
const VisualSearchTopSuggestions = ({ searchResultsData, searchResultsRef, ...props }) => {
  const { analyticsClient } = useContext(AnalyticsContext);
  useSuggestionShownAnalytics(props.searchTerm);

  return (
    <ul role="listbox">
      {searchResultsData?.map((searchResult, index) => (
        <VisualSearchTopSuggestionsItem
          analyticsClient={analyticsClient}
          key={index}
          searchResult={searchResult}
          searchResultsRef={searchResultsRef}
          {...props}
        />
      ))}
    </ul>
  );
};

export default VisualSearchTopSuggestions;

VisualSearchTopSuggestionsItem.propTypes = {
  searchResult: object,
  searchTerm: string,
  selectedSuggestion: object,
  setSelectedSuggestion: func,
  analyticsClient: object,
  searchResultsRef: object,
};

VisualSearchTopSuggestions.propTypes = {
  searchResultsData: array,
  searchTerm: string,
  selectedSuggestion: object,
  setSelectedSuggestion: func,
  searchResultsRef: object,
};
